import { type FC, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { Modal } from '../../components/Modal/Modal'
import { FormFeedback } from '../../components/Form/FormFeedback'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { RoleType } from '../../models/Role'
import { type InviteResponse, useBuyAndPromoteInvitesMutation } from '../../store/auth/auth'
import { selectAccountId } from '../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addToast } from '../../store/toast/toastSlice'
import { readBuyResult } from './buyResult'
import { seatTypeLabels } from './roleChangeSeatImpact'
import { SeatPurchaseSummary } from './SeatPurchaseSummary'
import { inviteName } from './teamRows'
import { useSeatPurchasePreview } from './useSeatPurchasePreview'

export interface BuySeatDialogProps {
  invitationId: string
  seatType: RoleType
  email: string
  firstLine: string
  secondLine: string
  centered?: boolean
  onDismiss: () => void
  onSeatsChanged: () => void
  onSent: () => void
  onGone: () => void
}

export const BuySeatDialog: FC<BuySeatDialogProps> = ({
  invitationId,
  seatType,
  email,
  firstLine,
  secondLine,
  centered = false,
  onDismiss,
  onSeatsChanged,
  onSent,
  onGone,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { trackingClient } = useTracking()
  const [buyAndPromoteInvites, { isLoading }] = useBuyAndPromoteInvitesMutation()
  const [error, setError] = useState<string | null>(null)
  const [seatBought, setSeatBought] = useState(false)

  const purchase = useMemo(() => ({ general: seatType === RoleType.SEAT ? 1 : 0, sidecar: seatType === RoleType.SIDECAR ? 1 : 0 }), [seatType])
  const preview = useSeatPurchasePreview(purchase)

  const handleConfirm = async () => {
    setError(null)
    let verdict
    try {
      const response = await buyAndPromoteInvites({ accountId, invitationIds: [invitationId], purchase }).unwrap()
      verdict = readBuyResult(response, invitationId, seatType)
    } catch {
      verdict = readBuyResult(undefined, invitationId, seatType)
    }

    onSeatsChanged()

    if (verdict.bought) {
      trackingClient.track({ eventName: TrackEvent.SEAT_PURCHASED, eventDetails: { email, seatType } })
      setSeatBought(true)
    }

    if (verdict.ok) {
      dispatch(addToast({ variant: 'success', title: verdict.bought ? 'Seat purchased. Invitation sent' : 'Invitation sent' }))
      onSent()
      return
    }

    if (verdict.closesModal) {
      if (verdict.message) {
        dispatch(addToast({ variant: 'info', title: verdict.message }))
      }
      onGone()
      return
    }

    setError(verdict.message ?? null)
  }

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Buy a seat</h4>
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <div className='paragraph-medium'>{firstLine}</div>
          <div className='paragraph-medium pb-2'>{secondLine}</div>
          <SeatPurchaseSummary preview={preview} />
          <div className='paragraph-small zocks-text-gray-600 pt-3'>
            You can also manage your seats on the <Link to='/account/plan'>Plan page</Link>.
          </div>
          {error ? <FormFeedback>{error}</FormFeedback> : null}
        </Col>
      </Row>
    </Container>
  )

  return (
    <Modal
      show
      onHide={() => {
        if (!isLoading) {
          onDismiss()
        }
      }}
      header={<></>}
      body={body}
      showFooterActions
      onFooterPrimaryAction={handleConfirm}
      footerPrimaryActionLoading={isLoading}
      footerPrimaryActionDisabled={seatBought}
      footerSecondaryActionDisabled={isLoading}
      footerPrimaryActionTitle='Confirm'
      centered={centered}
    />
  )
}

export interface BuySeatModalProps {
  invite: InviteResponse
  seatType: RoleType
  onHide: () => void
  onSeatsChanged: () => void
}

export const BuySeatModal: FC<BuySeatModalProps> = ({ invite, seatType, onHide, onSeatsChanged }) => (
  <BuySeatDialog
    invitationId={invite.id}
    seatType={seatType}
    email={invite.user.email}
    firstLine={`You're about to buy 1 ${seatTypeLabels[seatType]} seat for ${inviteName(invite.user)}.`}
    secondLine="Once you confirm, we'll send their invitation and their status changes to Pending."
    onDismiss={onHide}
    onSeatsChanged={onSeatsChanged}
    onSent={onHide}
    onGone={onHide}
  />
)
